import type { AppType as WorkersAppType } from '@rialtic/api'
import type { AuthHeaders } from '@rialtic/types'
import type { AppType } from '@worker/auth0-edge-access'
import { hc } from 'hono/client'
import { makeTracingHeaders } from '#imports'
import { useWorkspace } from '~/stores/workspace'

export const useEdgeAccessClient = (token?: string) => {
  const config = useRuntimeConfig().public

  const workspace = useWorkspace()

  return hc<AppType>(<string>config.AUTH0_ACCESS_API_BASE_URL, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-rialtic-workspace-id': workspace.workspaceId,
      'x-rialtic-connector-id': workspace.connectorId,
      workspace: workspace.workspaceId,
    },
  })
}

export const useRpcClient = (token?: string) => {
  const config = useRuntimeConfig().public
  const workspace = useWorkspace()

  return hc<WorkersAppType>(<string>config.WORKERS_API_BASE_URL, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-rialtic-workspace-id': workspace.workspaceId,
      'x-rialtic-connector-id': workspace.connectorId,
      'x-rialtic-impact-calculation-method':
        workspace.financialCalculationMethod,
      workspace: workspace.workspaceId,
    },
  })
}

export const useWorkersApi = () => {
  const config = useRuntimeConfig().public
  const { $auth0, $groundcover } = useNuxtApp()
  const workspace = useWorkspace()

  const baseURL = <string>config.WORKERS_API_BASE_URL

  const getAuthHeaders = async (): Promise<AuthHeaders | undefined> => {
    const { getAccessTokenSilently } = $auth0()
    const token = await getAccessTokenSilently()

    return {
      Authorization: `Bearer ${token}`,
      'x-rialtic-workspace-id': workspace.workspaceId,
      'x-rialtic-connector-id': workspace.connectorId,
      workspace: workspace.workspaceId,
      ...makeTracingHeaders(),
    }
  }

  const client = $fetch.create({
    baseURL,
    async onRequest({ options }) {
      const authHeaders = await getAuthHeaders()
      if (!authHeaders) {
        return
      }
      options.headers = {
        ...options.headers,
        ...authHeaders,
        ...makeTracingHeaders(),
      }
    },
    async onResponseError({
      error,
      response,
      options: { baseURL, params, query },
    }) {
      if (response.status === 401) {
        $groundcover?.sendCustomEvent({
          event: 'fetch_401',
          attributes: { baseURL, error, params, query },
        })
      } else if (response.status === 400) {
        $groundcover?.sendCustomEvent({
          event: 'fetch_400',
          attributes: { baseURL, error, params, query },
        })
      } else {
        $groundcover?.captureException(error as Error, {
          baseURL,
          params,
          query,
        })
      }
    },
  })

  const fetchPublic = $fetch.create({
    baseURL,

    async onRequest({ options }) {
      options.headers = {
        ...options.headers,
        ...makeTracingHeaders(),
      }
    },

    async onResponseError({
      error,
      response,
      options: { baseURL, params, query },
    }) {
      if (response.status === 400) {
        $groundcover?.sendCustomEvent({
          event: 'fetch_400',
          attributes: { baseURL, error, params, query },
        })
      } else {
        $groundcover?.captureException(error as Error, {
          baseURL,
          params,
          query,
        })
      }
    },
  })

  return {
    baseURL,
    fetch: client,
    fetchPublic,
    getAuthHeaders,
  }
}
